// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0 (FASE 5C.2)
// updated  : 2026-07-22
// protected: NO  -> UI Saldo Awal (Owner/Kacab). Backend 5A/5B/5B-2. Catat CHANGELOG saat diubah.
// changelog: v1.0 (Fase 5C.2) - UI alur DRAFT->VERIFIED->POSTED, region dari store,
//            tampil SKU di pemilihan produk, riwayat & status batch, region-aware,
//            RINGKASAN sebelum POST + PERINGATAN qty tidak wajar (>500), koreksi MoU (5B-2).
//            Mobile-friendly (kartu mobile / TableScroll desktop, pola Fase 4C).
// =============================================================================
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge as SharedStatusBadge, CategoryBadge } from "@/v2/components/statusMap";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Layers, Loader2, Plus, Trash2, CheckCircle2, Send, ShieldCheck, AlertTriangle, Info, Wrench,
  Upload, Download, FileSpreadsheet, Printer, Check, ChevronsUpDown,
} from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { STORES, isOwner, downloadFile } from "@/v2/components/helpers";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";
import { userRegion } from "@/v2/components/scope";
import { TableScroll } from "@/v2/components/TableScroll";

const SIZES = ["S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL", "7XL"];
const STORE_LABEL = { STORE_AREMA: "Store Arema (SBY)", STORE_BORNEO: "Store Borneo (SMD)" };
const REGION_OF_STORE = { STORE_AREMA: "SBY", STORE_BORNEO: "SMD" };
const STORE_OF_REGION = { SBY: "STORE_AREMA", SMD: "STORE_BORNEO" };
const YEARS = ["2025", "2026"];
// Ambang peringatan (bukan blokir). Revisi 5C.3:
// - PER BARIS: 1 produk+size dgn qty sangat besar = pola khas salah ketik.
// - Total batch: hanya INFO netral (saldo awal satu store penuh wajar ribuan unit).
const LINE_UNUSUAL_QTY = 200;   // peringatan per baris
const BATCH_LARGE_INFO = 5000;  // info netral "batch besar"

const OB_STATUS_LABEL = { DRAFT: "DRAFT", VERIFIED: "TERVERIFIKASI", POSTED: "TERPOSTING" };

const StatusBadge = ({ status }) => (
  <SharedStatusBadge status={status} label={OB_STATUS_LABEL[status] || status} testid="v2-ob-status" />
);

export default function SaldoAwalPage() {
  const { api, me, authHeader } = useV2Auth();
  const owner = isOwner(me?.role);
  // SLICE S-A2 (B-10): pakai helper KANONIK scope.js userRegion (satu definisi, satu tempat).
  // `effective_region` didahulukan agar Assist Mode tak regresi; userRegion menutup fallback
  // me.region -> REGION_BY_ROLE. Dulu keduanya kosong -> selalu STORE_AREMA walau peran Borneo.
  const region = me?.effective_region || userRegion(me);
  const defaultStore = owner ? "STORE_AREMA" : (STORE_OF_REGION[region] || "STORE_AREMA");

  const [batches, setBatches] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("opening-balance"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // form buat batch
  const [cStore, setCStore] = useState(defaultStore);
  const [cYear, setCYear] = useState("2026");
  const [cKind, setCKind] = useState("");
  const [cNote, setCNote] = useState("");
  const [creating, setCreating] = useState(false);

  // form baris
  const [lProduct, setLProduct] = useState("");
  const [lProductOpen, setLProductOpen] = useState(false);
  const [lSize, setLSize] = useState("");
  const [lQty, setLQty] = useState("");
  const [lKind, setLKind] = useState("");
  const [lNote, setLNote] = useState("");
  const [sizeQtys, setSizeQtys] = useState({});   // UAT-FIX-5 Slice 2: grid size->qty
  const [qtyNoSize, setQtyNoSize] = useState(""); // produk tanpa size
  const [busy, setBusy] = useState(false);

  // konfirmasi post + koreksi
  const [postOpen, setPostOpen] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjProduct, setAdjProduct] = useState("");
  const [adjSize, setAdjSize] = useState("");
  const [adjDelta, setAdjDelta] = useState("");
  // FASE 10.2: cetak label QR (JUAL-only, MoU dikecualikan)
  const [printOpen, setPrintOpen] = useState(false);
  const [printMeta, setPrintMeta] = useState(null);
  const [printMode, setPrintMode] = useState("thermal");
  const [printOffset, setPrintOffset] = useState("");
  const [printLimit, setPrintLimit] = useState("");
  const [printing, setPrinting] = useState(false);

  // import spreadsheet (5C.3)
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = React.useRef(null);
  const listTopRef = React.useRef(null);

  const loadBatches = useCallback(async (p = 1, size = pageSize) => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/opening-balance?page=${p}&page_size=${size}`);
      const data = await r.json().catch(() => ({}));
      if (r.ok) { setBatches(data.items || []); setMeta({ total: data.total || 0, pages: data.pages || 1 }); setPage(data.page || p); }
      else { toast.error(data.detail || "Gagal memuat batch saldo awal"); }
    } finally { setLoading(false); }
  }, [api, pageSize]);
  const goPage = (p) => { loadBatches(p); if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const changePageSize = (s) => { setPageSize(s); loadBatches(1, s); };

  const loadProducts = useCallback(async () => {
    const r = await api("/api/v2/merchandise/products");
    if (r.ok) { const d = await r.json(); setProducts((d.items || d || []).filter((p) => p.active)); }
  }, [api]);

  useEffect(() => { loadBatches(); loadProducts(); }, [loadBatches, loadProducts]);

  const refreshSelected = useCallback(async (id) => {
    const r = await api(`/api/v2/opening-balance/${id}`);
    if (r.ok) setSelected(await r.json());
    await loadBatches();
  }, [api, loadBatches]);

  // produk yang valid utk region batch (owner_region cocok)
  const productsForRegion = useMemo(() => {
    const reg = selected ? selected.region : REGION_OF_STORE[cStore];
    return products.filter((p) => !p.owner_region || p.owner_region === reg);
  }, [products, selected, cStore]);

  const productById = (id) => products.find((p) => p.id === id);
  const selProduct = productById(lProduct);
  const hasSizes = selProduct ? selProduct.has_size_variants !== false : true;
  // UAT-FIX-5 Slice 2: total unit dari grid (atau kotak tunggal utk produk tanpa size)
  const bulkTotal = hasSizes
    ? SIZES.reduce((s, sz) => s + (Number(sizeQtys[sz]) || 0), 0)
    : (Number(qtyNoSize) || 0);

  // pra-isi grid dari baris produk ini yang SUDAH ada di batch (edit ulang mulus)
  const prefillProduct = (pid) => {
    const mine = (selected?.lines || []).filter((l) => l.product_id === pid);
    const p = productById(pid);
    if (p && p.has_size_variants === false) {
      const t = mine.reduce((s, l) => s + (l.qty || 0), 0);
      setQtyNoSize(t ? String(t) : ""); setSizeQtys({});
    } else {
      const m = {};
      mine.forEach((l) => { if (l.size && l.size !== "-") m[l.size] = String(l.qty); });
      setSizeQtys(m); setQtyNoSize("");
    }
  };

  // ---------- actions ----------
  const createBatch = async () => {
    if (cKind !== "MOU" && cKind !== "JUAL") { toast.error("Pilih jenis batch (MOU atau JUAL) terlebih dahulu"); return; }
    setCreating(true);
    try {
      const body = { store: cStore, source_year: Number(cYear), default_kind: cKind };
      if (cNote.trim()) body.note = cNote.trim();
      const r = await api("/api/v2/opening-balance", { method: "POST", body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal membuat batch"); return; }
      toast.success(`Batch ${d.batch_no} dibuat (DRAFT)`);
      setCNote("");
      await loadBatches();
      setSelected(d); setDetailOpen(true);
    } finally { setCreating(false); }
  };

  const openBatch = async (b) => { setSelected(b); setDetailOpen(true); await refreshSelected(b.id); };

  const resetLineForm = () => { setLProduct(""); setLSize(""); setLQty(""); setLKind(""); setLNote(""); setSizeQtys({}); setQtyNoSize(""); };

  const addLinesBulk = async () => {
    if (!selected) return;
    if (!lProduct) { toast.error("Pilih produk"); return; }
    let entries = [];
    if (hasSizes) {
      entries = SIZES.map((s) => ({ size: s, qty: Number(sizeQtys[s] || 0) })).filter((e) => e.qty >= 1);
      if (!entries.length) { toast.error("Isi jumlah minimal satu size"); return; }
    } else {
      const q = Number(qtyNoSize || 0);
      if (q < 1) { toast.error("Isi jumlah"); return; }
      entries = [{ size: "-", qty: q }];
    }
    setBusy(true);
    try {
      const r = await api(`/api/v2/opening-balance/${selected.id}/lines/bulk`, {
        method: "POST",
        body: JSON.stringify({ product_id: lProduct, entries, note: lNote.trim() || null }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal menyimpan"); return; }
      toast.success(`${d.total_qty_added} unit disimpan (${d.added} baris)`);
      resetLineForm();
      await refreshSelected(selected.id);
    } finally { setBusy(false); }
  };

  const addLine = async () => {
    if (!selected) return;
    if (!lProduct) { toast.error("Pilih produk"); return; }
    const size = hasSizes ? lSize : "-";
    if (hasSizes && !size) { toast.error("Pilih size"); return; }
    const qty = Number(lQty);
    if (!qty || qty < 1) { toast.error("Qty harus > 0"); return; }
    const kind = lKind || selected.default_kind;
    if (!kind) { toast.error("Pilih jenis (MOU/JUAL)"); return; }
    setBusy(true);
    try {
      const r = await api(`/api/v2/opening-balance/${selected.id}/lines`, {
        method: "POST",
        body: JSON.stringify({ product_id: lProduct, size, qty, kind, note: lNote.trim() || null }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal menambah baris"); return; }
      toast.success("Baris ditambahkan");
      resetLineForm();
      await refreshSelected(selected.id);
    } finally { setBusy(false); }
  };

  const deleteLine = async (lineId) => {
    setBusy(true);
    try {
      const r = await api(`/api/v2/opening-balance/${selected.id}/lines/${lineId}`, { method: "DELETE" });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Gagal menghapus baris"); return; }
      await refreshSelected(selected.id);
    } finally { setBusy(false); }
  };

  const verifyBatch = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/v2/opening-balance/${selected.id}/verify`, { method: "PUT" });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal verifikasi"); return; }
      toast.success("Batch diverifikasi");
      setSelected(d); await loadBatches();
    } finally { setBusy(false); }
  };

  const doPost = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/v2/opening-balance/${selected.id}/post`, { method: "POST", body: "{}" });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal post"); return; }
      toast.success(`Batch diposting · ${d.posted_units || 0} unit QR terbentuk`);
      setPostOpen(false);
      setSelected(d); await loadBatches();
    } finally { setBusy(false); }
  };

  const submitAdjust = async () => {
    if (!adjProduct || !adjSize || !Number(adjDelta)) { toast.error("Lengkapi produk, size, dan delta (≠0)"); return; }
    setBusy(true);
    try {
      const r = await api(`/api/v2/opening-balance/${selected.id}/adjust`, {
        method: "POST",
        body: JSON.stringify({ lines: [{ product_id: adjProduct, size: adjSize, delta: Number(adjDelta) }] }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal koreksi"); return; }
      toast.success(`Koreksi MoU ${d.adjust_no}: void ${d.voided}, tambah ${d.added}`);
      setAdjOpen(false); setAdjProduct(""); setAdjSize(""); setAdjDelta("");
      await refreshSelected(selected.id);
    } finally { setBusy(false); }
  };

  // ---------- FASE 10.2: cetak label QR Saldo Awal (JUAL-only, MoU dikecualikan) ----------
  const openPrint = async (b) => {
    setPrintMeta(null); setPrintMode("thermal"); setPrintOffset(""); setPrintLimit("");
    setPrintOpen(true);
    try {
      const r = await api(`/api/v2/opening-balance/${b.id}/labels/meta`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memuat info label"); return; }
      setPrintMeta(d);
    } catch (e) { toast.error("Gagal memuat info label"); }
  };

  const doPrint = async () => {
    if (!selected) return;
    setPrinting(true);
    const tid = toast.loading("Menyiapkan label PDF...");
    try {
      const params = new URLSearchParams({ mode: printMode });
      if (Number(printOffset) > 0) params.set("offset", String(Number(printOffset)));
      if (Number(printLimit) > 0) params.set("limit", String(Number(printLimit)));
      const r = await api(`/api/v2/opening-balance/${selected.id}/labels.pdf?${params.toString()}`);
      if (!r.ok) {
        let msg = "Gagal membuat label";
        try { msg = (await r.json()).detail || msg; } catch (e) { /* noop */ }
        toast.error(typeof msg === "string" ? msg : "Gagal membuat label", { id: tid });
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {  // popup diblokir -> unduh sebagai fallback
        const a = document.createElement("a");
        a.href = url; a.download = `labels-saldo-${selected.batch_no}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Label PDF siap — silakan cetak (skala 100%)", { id: tid });
    } catch (e) {
      toast.error("Gagal membuat label", { id: tid });
    } finally { setPrinting(false); }
  };


  // ---------- import spreadsheet (5C.3) ----------
  const downloadTemplate = () => downloadFile(api, "/api/v2/opening-balance/import/template", "template_saldo_awal.xlsx");

  const doImport = async (fileObj) => {
    if (!fileObj) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/opening-balance/import`, {
        method: "POST", headers: { Authorization: authHeader().Authorization }, body: fd,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal import"); return; }
      setImportResult(d);
      if (d.valid_rows > 0) toast.success(`${d.valid_rows} baris valid → ${d.batches.length} batch DRAFT dibuat`);
      if (d.rejected_rows > 0) toast.warning(`${d.rejected_rows} baris ditolak (lihat detail)`);
      await loadBatches();
    } finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // ---------- ringkasan pre-post ----------
  const summary = useMemo(() => {
    const lines = selected?.lines || [];
    const mou = lines.filter((l) => l.kind === "MOU").reduce((s, l) => s + l.qty, 0);
    const jual = lines.filter((l) => l.kind === "JUAL").reduce((s, l) => s + l.qty, 0);
    const bigLines = lines.filter((l) => l.qty > LINE_UNUSUAL_QTY);
    return { total: mou + jual, mou, jual, bigLines };
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="v2-saldo-awal-page">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2"><Layers className="w-6 h-6" />Saldo Awal</h2>
        <div className="flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm mt-3">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
          <p>Masukkan stok fisik yang sudah ada di store <b>sebelum sistem berjalan</b>. Alur: buat <b>DRAFT</b> → tambah baris → <b>verifikasi</b> → <b>post</b> (menulis ledger &amp; membuat QR per unit; permanen). Region diturunkan otomatis dari store.</p>
        </div>
        {/* UAT-FIX-5 Slice 3: keterangan TEGAS — saldo awal hanya untuk barang BELUM TERJUAL. */}
        <div className="flex items-start gap-2 rounded-xl border border-[hsl(var(--badge-warning))]/40 bg-[hsl(var(--badge-warning))]/10 px-4 py-3 text-sm mt-2 text-[hsl(var(--badge-warning))]" data-testid="v2-ob-notice-unsold">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--badge-warning))]" />
          <p><b>Saldo awal hanya untuk barang yang BELUM TERJUAL.</b> Barang yang sudah terjual sebelum sistem berjalan tidak dapat didata di sini — administrasi, invoice, dan penagihannya ditangani manual di luar aplikasi.</p>
        </div>
      </div>

      {/* ---- Buat Batch ---- */}
      <Card className="p-4 sm:p-5 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><Plus className="w-4 h-4" />Buat Batch Baru</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Store</Label>
            <Select value={cStore} onValueChange={setCStore} disabled={!owner}>
              <SelectTrigger data-testid="v2-ob-create-store"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(owner ? STORES : [defaultStore]).map((s) => (
                  <SelectItem key={s} value={s}>{STORE_LABEL[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tahun sumber</Label>
            <Select value={cYear} onValueChange={setCYear}>
              <SelectTrigger data-testid="v2-ob-create-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Jenis <span className="text-destructive">*</span></Label>
            <Select value={cKind} onValueChange={setCKind}>
              <SelectTrigger data-testid="v2-ob-create-kind"><SelectValue placeholder="Pilih MOU / JUAL" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MOU">MOU</SelectItem>
                <SelectItem value="JUAL">JUAL</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Satu batch = satu jenis. Semua baris mengikuti jenis ini.</p>
          </div>
          <div>
            <Label className="text-xs">Catatan</Label>
            <Input value={cNote} onChange={(e) => setCNote(e.target.value)} placeholder="opsional" data-testid="v2-ob-create-note" />
          </div>
        </div>
        <Button onClick={createBatch} disabled={creating || (cKind !== "MOU" && cKind !== "JUAL")} data-testid="v2-ob-create-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Buat Batch (DRAFT)
        </Button>
      </Card>

      {/* ---- Import Spreadsheet (5C.3) ---- */}
      <Card className="p-4 sm:p-5 space-y-3">
        <h3 className="font-semibold flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" />Import Spreadsheet</h3>
        <p className="text-xs text-muted-foreground">Kolom baku: <code>store, tahun, jenis, sku, nama_produk, size, qty, harga, catatan</code>. Region diturunkan dari store. Produk dikenali via <b>SKU</b> (utama) atau <b>nama+klub</b> (fallback). Baris valid → <b>batch DRAFT</b> (tetap lewat verifikasi &amp; post).</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="v2-ob-import-template">
            <Download className="w-4 h-4 mr-2" />Unduh Template
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
            onChange={(e) => doImport(e.target.files?.[0])} data-testid="v2-ob-import-file" />
          <Button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="v2-ob-import-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}Pilih File &amp; Import
          </Button>
        </div>
        {importResult && (
          <div className="rounded-xl border p-3 text-sm space-y-2" data-testid="v2-ob-import-result">
            <div className="flex flex-wrap gap-3">
              <Badge variant="secondary">Total: {importResult.total_rows}</Badge>
              <Badge variant="success">Valid: {importResult.valid_rows}</Badge>
              <Badge className="bg-destructive/15 text-destructive">Ditolak: {importResult.rejected_rows}</Badge>
            </div>
            {importResult.batches?.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1">Batch DRAFT dibuat:</p>
                <ul className="text-xs space-y-1">
                  {importResult.batches.map((b) => (
                    <li key={b.id} className="flex items-center justify-between gap-2">
                      <span className="font-mono">{b.batch_no} · {STORE_LABEL[b.store] || b.store} · {b.source_year} · {b.total_qty} unit</span>
                      <Button size="sm" variant="ghost" onClick={() => openBatch(b)} data-testid="v2-ob-import-open">Buka</Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {importResult.errors?.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1 text-destructive">Baris ditolak (perbaiki lalu import ulang):</p>
                <div className="max-h-48 overflow-y-auto rounded-lg border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50"><tr><th className="text-left px-2 py-1">Baris</th><th className="text-left px-2 py-1">Alasan</th></tr></thead>
                    <tbody>
                      {importResult.errors.map((er, i) => (
                        <tr key={i} className="border-t" data-testid="v2-ob-import-error-row">
                          <td className="px-2 py-1 font-mono align-top">{er.row}</td>
                          <td className="px-2 py-1">
                            {er.reason}
                            {er.candidates?.length > 0 && (
                              <span className="text-muted-foreground"> — kandidat: {er.candidates.join("; ")}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ---- Riwayat Batch ---- */}
      <Card className="p-4 sm:p-5" ref={listTopRef}>
        <h3 className="font-semibold mb-3">Riwayat Batch ({meta.total})</h3>
        {loading && batches.length === 0 && (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        )}
        {batches.length > 0 && (
          <>
            {/* mobile: kartu */}
            <div className="space-y-3 md:hidden">
              {batches.map((b) => (
                <button key={b.id} onClick={() => openBatch(b)} data-testid="v2-ob-batch-card"
                  className="w-full text-left rounded-xl border p-3 hover:bg-accent/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-medium">{b.batch_no}</span>
                    <StatusBadge status={b.status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {STORE_LABEL[b.store] || b.store} · {b.source_year} · {b.line_count} baris · {b.total_qty} unit
                  </div>
                </button>
              ))}
            </div>
            {/* desktop: tabel */}
            <div className="hidden md:block">
              <TableScroll>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>No. Batch</TableHead>
                      <TableHead>Store</TableHead>
                      <TableHead>Tahun</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Baris</TableHead>
                      <TableHead className="text-right">Total Unit</TableHead>
                      <TableHead className="text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.map((b) => (
                      <TableRow key={b.id} data-testid="v2-ob-batch-row">
                        <TableCell className="font-mono">{b.batch_no}</TableCell>
                        <TableCell>{STORE_LABEL[b.store] || b.store}</TableCell>
                        <TableCell>{b.source_year}</TableCell>
                        <TableCell><StatusBadge status={b.status} /></TableCell>
                        <TableCell className="text-right">{b.line_count}</TableCell>
                        <TableCell className="text-right font-medium">{b.total_qty}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => openBatch(b)} data-testid="v2-ob-open-btn">Buka</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableScroll>
            </div>
          </>
        )}
        <ListPager
          page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={loading}
          onPageChange={goPage} onPageSizeChange={changePageSize}
          storageKey="opening-balance" testid="saldo-awal-pager"
        />
      </Card>

      {/* ---- Detail Batch ---- */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl" data-testid="v2-ob-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono">{selected.batch_no}</span>
                  <StatusBadge status={selected.status} />
                  {selected.default_kind
                    ? <CategoryBadge cat={selected.default_kind} testid="v2-ob-batch-kind" />
                    : <span className="text-[11px] text-muted-foreground" data-testid="v2-ob-batch-kind-legacy">Jenis per baris (batch lama)</span>}
                </DialogTitle>
                <DialogDescription>
                  {STORE_LABEL[selected.store] || selected.store} · Tahun {selected.source_year} · Region {selected.region}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
                {/* form tambah baris (DRAFT) */}
                {selected.status === "DRAFT" && (
                  <div className="rounded-xl border p-3 space-y-3">
                    <p className="text-xs font-medium">Tambah Baris</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Produk (cari SKU · nama · klub)</Label>
                        <Popover open={lProductOpen} onOpenChange={setLProductOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" className="w-full justify-between font-normal mt-1"
                              data-testid="v2-ob-line-product">
                              <span className="truncate text-left">
                                {selProduct
                                  ? `${selProduct.sku ? selProduct.sku + " · " : ""}${selProduct.name} (${selProduct.club})`
                                  : "Pilih / cari produk"}
                              </span>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
                            <Command>
                              <CommandInput placeholder="Ketik SKU / nama / klub..." data-testid="v2-ob-line-product-search" />
                              <CommandList>
                                <CommandEmpty>Produk tidak ditemukan.</CommandEmpty>
                                <CommandGroup>
                                  {productsForRegion.map((p) => (
                                    <CommandItem
                                      key={p.id}
                                      value={`${p.sku || ""} ${p.name} ${p.club}`}
                                      onSelect={() => { setLProduct(p.id); prefillProduct(p.id); setLProductOpen(false); }}
                                      data-testid="v2-ob-line-product-option"
                                    >
                                      <Check className={`mr-2 h-4 w-4 ${lProduct === p.id ? "opacity-100" : "opacity-0"}`} />
                                      <span className="flex flex-col">
                                        <span className="text-sm">{p.name}</span>
                                        <span className="text-[11px] text-muted-foreground">
                                          {p.sku ? p.sku + " · " : ""}{p.club}
                                        </span>
                                      </span>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                      {/* jenis: mengikuti batch (SATU BATCH = SATU JENIS). Batch lama -> pilih. */}
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Jenis</Label>
                        {selected.default_kind ? (
                          <div className="mt-1 flex items-center gap-2">
                            <CategoryBadge cat={selected.default_kind} testid="v2-ob-line-kind-badge" />
                            <span className="text-[11px] text-muted-foreground">Semua baris mengikuti jenis batch.</span>
                          </div>
                        ) : (
                          <Select value={lKind} onValueChange={setLKind}>
                            <SelectTrigger data-testid="v2-ob-line-kind"><SelectValue placeholder="MOU/JUAL (batch lama)" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="MOU">MOU</SelectItem>
                              <SelectItem value="JUAL">JUAL</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    {/* UAT-FIX-5 Slice 2: pilih produk SEKALI -> isi SEMUA size sekaligus.
                        has_size_variants=false (rompi/kaos kaki/syal/topi) -> satu kotak jumlah, tanpa size.
                        Size dengan jumlah 0/kosong tidak menjadi baris. */}
                    {lProduct && (
                      hasSizes ? (
                        <div data-testid="v2-ob-size-grid">
                          <Label className="text-xs">Size &amp; Jumlah <span className="text-muted-foreground">(0/kosong dilewati)</span></Label>
                          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-1">
                            {SIZES.map((s) => (
                              <div key={s}>
                                <div className="text-[10px] text-center text-muted-foreground">{s}</div>
                                <Input type="number" min="0" inputMode="numeric" value={sizeQtys[s] || ""}
                                  onChange={(e) => setSizeQtys((m) => ({ ...m, [s]: e.target.value }))}
                                  className="text-center px-1" placeholder="0" data-testid={`v2-ob-size-${s}`} />
                              </div>
                            ))}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1">Total: <b data-testid="v2-ob-bulk-total">{bulkTotal}</b> unit</p>
                        </div>
                      ) : (
                        <div className="max-w-[220px]" data-testid="v2-ob-nosize">
                          <Label className="text-xs">Jumlah <span className="text-muted-foreground">(produk tanpa size)</span></Label>
                          <Input type="number" min="0" inputMode="numeric" value={qtyNoSize}
                            onChange={(e) => setQtyNoSize(e.target.value)} placeholder="0" data-testid="v2-ob-qty-nosize" />
                        </div>
                      )
                    )}

                    <div>
                      <Label className="text-xs">Catatan (opsional)</Label>
                      <Input value={lNote} onChange={(e) => setLNote(e.target.value)} placeholder="opsional" data-testid="v2-ob-line-note" />
                    </div>

                    <Button size="sm" onClick={addLinesBulk} disabled={busy || !lProduct || bulkTotal < 1} data-testid="v2-ob-line-add">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Isi ke Batch
                    </Button>
                  </div>
                )}

                {/* daftar baris */}
                <div>
                  <p className="text-xs font-medium mb-2">Baris ({selected.line_count}) · Total {selected.total_qty} unit</p>
                  {(selected.lines || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground py-3 text-center border rounded-lg">Belum ada baris.</p>
                  ) : (
                    <div className="space-y-2">
                      {selected.lines.map((l) => {
                        const p = productById(l.product_id);
                        return (
                          <div key={l.id} className="flex items-center justify-between gap-2 rounded-lg border p-2 text-sm" data-testid="v2-ob-line-item">
                            <div className="min-w-0">
                              <div className="truncate font-medium">{p ? p.name : (l.sku || "Produk")}</div>
                              <div className="text-xs text-muted-foreground">
                                {l.sku ? l.sku + " · " : ""}{l.size} · {l.qty} unit · <CategoryBadge cat={l.kind} className="text-[10px]" />
                              </div>
                            </div>
                            {selected.status === "DRAFT" && (
                              <Button size="icon" variant="ghost" onClick={() => deleteLine(l.id)} disabled={busy} data-testid="v2-ob-line-del">
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selected.status === "POSTED" && (
                  <div className="rounded-lg border border-[hsl(var(--badge-success))]/30 bg-[hsl(var(--badge-success))]/5 p-3 text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[hsl(var(--badge-success))]" />
                    {(selected.lines || []).some((l) => l.kind === "MOU")
                      ? "Batch sudah diposting ke ledger. Koreksi MoU memakai mekanik adjustment (append-only)."
                      : "Batch sudah diposting ke ledger. Koreksi JUAL memakai Stock Opname."}
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selected.status === "DRAFT" && (
                  <Button onClick={verifyBatch} disabled={busy || (selected.lines || []).length === 0} data-testid="v2-ob-verify-btn">
                    <ShieldCheck className="w-4 h-4 mr-2" />Verifikasi
                  </Button>
                )}
                {selected.status === "VERIFIED" && (
                  <Button onClick={() => setPostOpen(true)} disabled={busy} data-testid="v2-ob-post-open-btn">
                    <Send className="w-4 h-4 mr-2" />Post Batch
                  </Button>
                )}
                {selected.status === "POSTED" && (
                  <>
                    <Button onClick={() => openPrint(selected)} data-testid="v2-ob-print-open-btn">
                      <Printer className="w-4 h-4 mr-2" />Cetak Label
                    </Button>
                    {/* D.2 (UAT-FIX-3): tombol Koreksi MoU hanya bila batch berisi baris MoU. */}
                    {(selected.lines || []).some((l) => l.kind === "MOU") && (
                      <Button variant="outline" onClick={() => setAdjOpen(true)} data-testid="v2-ob-adjust-open-btn">
                        <Wrench className="w-4 h-4 mr-2" />Koreksi MoU
                      </Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Konfirmasi POST (ringkasan + peringatan) ---- */}
      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent className="max-w-md" data-testid="v2-ob-post-confirm">
          <DialogHeader>
            <DialogTitle>Konfirmasi Post Saldo Awal</DialogTitle>
            <DialogDescription>Tindakan ini menulis ledger (append-only) &amp; membuat QR per unit. Tidak bisa dibatalkan — hanya bisa dikoreksi lewat adjustment/opname.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="rounded-xl border p-3 text-sm space-y-1.5" data-testid="v2-ob-post-summary">
                <div className="flex justify-between"><span className="text-muted-foreground">Store</span><span className="font-medium">{STORE_LABEL[selected.store] || selected.store}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tahun</span><span className="font-medium">{selected.source_year}</span></div>
                <div className="flex justify-between border-t pt-1.5"><span className="text-muted-foreground">Total unit akan dibuat</span><span className="font-semibold" data-testid="v2-ob-post-total">{summary.total} unit QR</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">— MOU</span><span data-testid="v2-ob-post-mou">{summary.mou} unit</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">— JUAL</span><span data-testid="v2-ob-post-jual">{summary.jual} unit</span></div>
              </div>
              {summary.total > BATCH_LARGE_INFO && (
                <div className="flex items-start gap-2 rounded-lg border bg-muted/50 p-3 text-sm" data-testid="v2-ob-post-biginfo">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <span>Batch besar: <b>{summary.total} unit</b>. Wajar untuk memuat stok satu store penuh — pastikan sesuai rencana.</span>
                </div>
              )}
              {summary.bigLines.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-[hsl(var(--badge-warning))]/40 bg-[hsl(var(--badge-warning))]/10 p-3 text-sm text-[hsl(var(--badge-warning))]" data-testid="v2-ob-post-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p><b>{summary.bigLines.length} baris</b> punya qty besar (&gt; {LINE_UNUSUAL_QTY}/baris) — pola khas salah ketik. Pastikan benar:</p>
                    <ul className="mt-1 list-disc pl-4 text-xs">
                      {summary.bigLines.slice(0, 5).map((l) => (
                        <li key={l.id}>{(l.sku ? l.sku + " · " : "") + l.size} — <b>{l.qty} unit</b> ({l.kind})</li>
                      ))}
                      {summary.bigLines.length > 5 && <li>…dan {summary.bigLines.length - 5} baris lain</li>}
                    </ul>
                    <p className="mt-1">Ini bukan blokir — lanjutkan hanya bila yakin.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setPostOpen(false)} disabled={busy} data-testid="v2-ob-post-cancel">Batal</Button>
            <Button onClick={doPost} disabled={busy} data-testid="v2-ob-post-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Ya, Post Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Koreksi MoU (5B-2) ---- */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="max-w-md" data-testid="v2-ob-adjust-dialog">
          <DialogHeader>
            <DialogTitle>Koreksi Saldo Awal MoU</DialogTitle>
            <DialogDescription>Hanya untuk item <b>MoU</b>. Delta negatif = kurangi (void), positif = tambah. Koreksi JUAL memakai Stock Opname.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Produk</Label>
                <Select value={adjProduct} onValueChange={setAdjProduct}>
                  <SelectTrigger data-testid="v2-ob-adjust-product"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                  <SelectContent>
                    {productsForRegion.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{(p.sku ? p.sku + " · " : "") + p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Size</Label>
                  <Select value={adjSize} onValueChange={setAdjSize}>
                    <SelectTrigger data-testid="v2-ob-adjust-size"><SelectValue placeholder="Size" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-">Tanpa size</SelectItem>
                      {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Delta (mis. -2 / +3)</Label>
                  <Input type="number" value={adjDelta} onChange={(e) => setAdjDelta(e.target.value)} placeholder="0" data-testid="v2-ob-adjust-delta" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAdjOpen(false)} disabled={busy}>Batal</Button>
            <Button onClick={submitAdjust} disabled={busy} data-testid="v2-ob-adjust-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wrench className="w-4 h-4 mr-2" />}Terapkan Koreksi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- FASE 10.2: Cetak Label QR (JUAL-only, MoU dikecualikan) ---- */}
      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="max-w-md" data-testid="v2-ob-print-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5" />Cetak Label QR</DialogTitle>
            <DialogDescription>
              Label thermal 50×40 mm (300 dpi). <b>Semua barang dicetak</b> — JUAL &amp; MoU
              (badge MoU dibedakan warnanya). Bisa dicetak ulang kapan saja.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              {printMeta ? (
                <div className="rounded-xl border p-3 text-sm space-y-1.5" data-testid="v2-ob-print-summary">
                  <div className="flex justify-between"><span className="text-muted-foreground">Batch</span><span className="font-mono">{printMeta.batch_no}</span></div>
                  <div className="flex justify-between border-t pt-1.5"><span className="text-muted-foreground">Total akan dicetak</span><span className="font-semibold" data-testid="v2-ob-print-total">{printMeta.will_print} label</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">JUAL</span><span className="font-semibold text-[hsl(var(--badge-success))]" data-testid="v2-ob-print-jual">{printMeta.jual_count}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">MoU</span><span className="font-semibold text-[hsl(var(--badge-warning))]" data-testid="v2-ob-print-mou">{printMeta.mou_count}</span></div>
                  <p className="text-xs text-muted-foreground pt-1" data-testid="v2-ob-print-message">{printMeta.message}</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-3"><Loader2 className="w-4 h-4 animate-spin" />Memuat info label…</div>
              )}

              <div>
                <Label className="text-xs">Mode cetak</Label>
                <Select value={printMode} onValueChange={setPrintMode}>
                  <SelectTrigger className="mt-1" data-testid="v2-ob-print-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="thermal">Thermal 50×40 mm (1 label/halaman)</SelectItem>
                    <SelectItem value="a4">A4 darurat (dengan garis potong)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Mulai dari (opsional)</Label>
                  <Input type="number" min="0" value={printOffset} onChange={(e) => setPrintOffset(e.target.value)} placeholder="0" data-testid="v2-ob-print-offset" />
                </div>
                <div>
                  <Label className="text-xs">Jumlah (kosong = semua)</Label>
                  <Input type="number" min="0" value={printLimit} onChange={(e) => setPrintLimit(e.target.value)} placeholder="semua" data-testid="v2-ob-print-limit" />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">Cetak sebagian: mis. mulai 0 jumlah 50 untuk 50 label pertama.</p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setPrintOpen(false)} disabled={printing}>Tutup</Button>
            <Button onClick={doPrint} disabled={printing || !printMeta || (printMeta && printMeta.will_print === 0)} data-testid="v2-ob-print-submit">
              {printing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Printer className="w-4 h-4 mr-2" />}Buka PDF &amp; Cetak
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
